import React, { useState } from 'react';
import { CheckCircle2, AlertCircle, Send } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { track } from '@/lib/tracking';
import StarRating from '@/components/testimonials/StarRating';
import {
  CustomerType,
  submitTestimonial,
  validateTestimonial,
} from '@/lib/testimonials';

const input =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-orange-100';
const label = 'block text-sm font-bold text-slate-800';

/**
 * Bilingual review form. Every field in the spec, in spec order:
 * name, city, customer type, rating, testimonial, recommend, permission.
 *
 * Validation lives in src/lib/testimonials.ts so this form and the database
 * CHECK constraints agree; the submit always lands as status='pending'.
 */
const TestimonialForm: React.FC<{ onSubmitted?: () => void; source?: string }> = ({
  onSubmitted,
  source = 'testimonials-page',
}) => {
  const { t, lang } = useI18n();
  const { user, profile } = useAuth();

  const prefillName =
    profile?.company_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    '';

  const [name, setName] = useState(prefillName);
  const [city, setCity] = useState(profile?.city || '');
  const [state, setState] = useState(profile?.state || '');
  const [customerType, setCustomerType] = useState<CustomerType>(
    profile?.user_type === 'homeowner' ? 'homeowner' : 'contractor'
  );
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState('');
  const [recommend, setRecommend] = useState(true);
  const [email, setEmail] = useState(profile?.email || user?.email || '');
  const [permission, setPermission] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [done, setDone] = useState(false);

  const reset = () => {
    setBody('');
    setRating(5);
    setPermission(false);
    setErrors({});
    setSaveError('');
    setDone(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');

    const draft = {
      name,
      city,
      state,
      customer_type: customerType,
      rating,
      body,
      recommend,
      permission,
      language: lang,
      email,
      user_id: user?.id || null,
      source,
    };

    const check = validateTestimonial(draft);
    setErrors(check.errors);
    if (!check.ok) return;

    setSaving(true);
    const { error } = await submitTestimonial(draft);
    setSaving(false);

    if (error) {
      setSaveError(t('testi.errSave'));
      return;
    }
    track('form_completed', { form: 'testimonial', rating, customerType, source });
    setDone(true);
    onSubmitted?.();
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center sm:p-8">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" />
        <h3 className="mt-3 text-xl font-extrabold text-emerald-900">{t('testi.thanksTitle')}</h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-emerald-800">
          {t('testi.thanksBody')}
        </p>
        <button
          type="button"
          onClick={reset}
          className="mt-5 inline-flex items-center justify-center rounded-lg border border-emerald-300 bg-white px-4 py-2.5 text-sm font-bold text-emerald-800 transition-colors hover:bg-emerald-100"
        >
          {t('testi.writeAnother')}
        </button>
      </div>
    );
  }

  const err = (k: string) =>
    errors[k] ? (
      <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-red-600">
        <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
        {t(errors[k])}
      </p>
    ) : null;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card sm:p-8"
    >
      <h3 className="text-xl font-extrabold text-slate-900">{t('testi.formTitle')}</h3>
      <p className="mt-1 text-sm text-slate-500">{t('testi.formSub')}</p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        {/* 1. Name */}
        <div className="sm:col-span-2">
          <label className={label} htmlFor="testi-name">
            {t('testi.name')} *
          </label>
          <input
            id="testi-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('testi.namePh')}
            className={`mt-1.5 ${input}`}
            autoComplete="name"
          />
          <p className="mt-1 text-xs text-slate-500">{t('testi.nameHelp')}</p>
          {err('name')}
        </div>

        {/* 2. City (+ optional state) */}
        <div>
          <label className={label} htmlFor="testi-city">
            {t('testi.city')} *
          </label>
          <input
            id="testi-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t('testi.cityPh')}
            className={`mt-1.5 ${input}`}
            autoComplete="address-level2"
          />
          {err('city')}
        </div>
        <div>
          <label className={label} htmlFor="testi-state">
            {t('testi.state')}
          </label>
          <input
            id="testi-state"
            value={state}
            onChange={(e) => setState(e.target.value.slice(0, 20))}
            placeholder={t('testi.statePh')}
            className={`mt-1.5 ${input}`}
            autoComplete="address-level1"
          />
        </div>

        {/* 3. Customer type */}
        <div className="sm:col-span-2">
          <span className={label}>{t('testi.customerType')} *</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {(['homeowner', 'contractor'] as CustomerType[]).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={customerType === value}
                onClick={() => setCustomerType(value)}
                className={`rounded-lg border px-4 py-2.5 text-sm font-bold transition-all ${
                  customerType === value
                    ? 'border-brand-navy bg-brand-navy text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                }`}
              >
                {value === 'homeowner' ? t('testi.typeHomeowner') : t('testi.typeContractor')}
              </button>
            ))}
          </div>
        </div>

        {/* 4. Rating */}
        <div className="sm:col-span-2">
          <span className={label}>{t('testi.rating')} *</span>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <StarRating value={rating} onChange={setRating} size="lg" />
            <span className="text-sm font-semibold text-slate-600">
              {t('testi.ofFive', { rating })}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">{t('testi.ratingHelp')}</p>
          {err('rating')}
        </div>

        {/* 5. Testimonial */}
        <div className="sm:col-span-2">
          <label className={label} htmlFor="testi-body">
            {t('testi.body')} *
          </label>
          <textarea
            id="testi-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('testi.bodyPh')}
            rows={5}
            maxLength={2000}
            className={`mt-1.5 ${input}`}
          />
          <p className="mt-1 text-right text-xs text-slate-400">{body.length}/2000</p>
          {err('body')}
        </div>

        {/* 6. Recommend */}
        <div className="sm:col-span-2">
          <span className={label}>{t('testi.recommendQ')}</span>
          <div className="mt-2 flex gap-2">
            {[true, false].map((v) => (
              <button
                key={String(v)}
                type="button"
                aria-pressed={recommend === v}
                onClick={() => setRecommend(v)}
                className={`rounded-lg border px-5 py-2.5 text-sm font-bold transition-all ${
                  recommend === v
                    ? v
                      ? 'border-emerald-600 bg-emerald-600 text-white'
                      : 'border-slate-600 bg-slate-600 text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                }`}
              >
                {v ? t('testi.yes') : t('testi.no')}
              </button>
            ))}
          </div>
        </div>

        {/* Optional email — used only to verify a review, never published. */}
        <div className="sm:col-span-2">
          <label className={label} htmlFor="testi-email">
            {t('testi.email')}
          </label>
          <input
            id="testi-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`mt-1.5 ${input}`}
            autoComplete="email"
          />
          <p className="mt-1 text-xs text-slate-500">{t('testi.emailHelp')}</p>
          {err('email')}
        </div>

        {/* 7. Permission */}
        <div className="sm:col-span-2">
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <input
              type="checkbox"
              checked={permission}
              onChange={(e) => setPermission(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-400 text-brand-orange focus:ring-brand-orange"
            />
            <span className="text-sm leading-relaxed text-slate-700">{t('testi.permission')}</span>
          </label>
          {err('permission')}
        </div>
      </div>

      {saveError && (
        <p className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          {saveError}
        </p>
      )}

      <button
        type="submit"
        disabled={saving}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 py-3.5 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        <Send className="h-4 w-4" aria-hidden="true" />
        {saving ? t('testi.submitting') : t('testi.submit')}
      </button>

      <p className="mt-4 text-xs leading-relaxed text-slate-500">{t('testi.moderationNote')}</p>
    </form>
  );
};

export default TestimonialForm;
